import React, { FC, useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Center, Stack, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { join } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { StackedCell, StackedHeader } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { DownloadIcon, ListIcon } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { renderImageName } from 'containers/Visibility/Vulnerabilities/Components/hooks/utils';
import { ImageContainers } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ImageContainers';
import { ResourceVulnerabilityDetailsTable } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilityDetailsTable';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { saveAsFile } from 'utils/download';

interface Props {}

export const ContainerImages: FC<Props> = props => {
  const {
    ecrImages,
    resourceInsight,
    downloadReport,
    useVulnerabilityV3,
  } = useSelector(selectVulnerabilities);
  const { environmentId, imageName } = useSearchParams<{
    environmentId: string;
    imageName: string;
  }>();
  const params = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [finalSearchTerm, setFinalSearchTerm] = useState('');
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const showContainers = useDisclosure();
  const showVulnerabilities = useDisclosure();

  useEffect(() => {
    if (!!imageName) {
      setFinalSearchTerm(imageName);
    }
  }, [imageName]);

  useEffect(() => {
    setSearchTerm(finalSearchTerm);
  }, [finalSearchTerm]);

  useEffect(() => {
    dispatch(
      actions.getECRImages({
        q: {
          environmentId,
          page: ecrImages.page.info.page_number,
          pageSize: ecrImages.page.info.page_size,
          repositoryUuid: params.id,
          searchTerm: !!finalSearchTerm ? finalSearchTerm : undefined,
        },
      }),
    );
  }, [
    environmentId,
    dispatch,
    ecrImages.page.info,
    params.id,
    finalSearchTerm,
  ]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      render: ({ row }) =>
        renderImageName(row, NativeResources.ElasticContainerRegistry),
    },
    {
      header: 'Registry',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.registry_id}
          lower={row?.repository_arn ?? row?.repository_name}
        />
      ),
    },
    {
      header: 'Status',
      align: 'left',
      render: ({ row }) => {
        return row?.image_scan_status;
      },
    },
    {
      header: 'Summary',
      align: 'left',
      render: ({ row }) => {
        return renderVulnerabilities({ value: row?.vulnerabilities });
      },
    },

    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            icon={<ListIcon />}
            label="Show containers"
            onClick={e => {
              e.stopPropagation();
              showContainers.onOpen();
              setCurrentRecord(row);
            }}
          />
          <ActionButton
            icon={<DownloadIcon />}
            label="Download vulnerabilities in CSV"
            isLoading={
              downloadReport.isLoading && currentRecord?.id === row?.id
            }
            onClick={e => {
              e.stopPropagation();
              setCurrentRecord(row);
              dispatch(
                actions.downloadReport({
                  q: {
                    accountId: [resourceInsight.data?.account],
                    environmentId,
                    resourceId: [row?.id],
                  },
                  onSuccess: res => {
                    saveAsFile(
                      res,
                      'Ariksa - Vulnerabilities_' +
                        row?.id +
                        '_' +
                        dayjs().format('DD-MMM-YYYY h.mm A') +
                        '.csv',
                      'text/csv;charset=utf-8;',
                    );
                  },
                }),
              );
            }}
          />
        </Center>
      ),
    },
  ];

  return (
    <Box h="full" minH="400px">
      <Stack h="full" spacing={5}>
        <Box w="30%">
          <SearchBox
            value={searchTerm}
            onChange={s => {
              setSearchTerm(s);
              if (!s) setFinalSearchTerm('');
            }}
            onSearch={() => setFinalSearchTerm(searchTerm)}
            onClickClose={() => setFinalSearchTerm('')}
          />
        </Box>
        <Box flex={1}>
          <Table
            columns={columns}
            data={ecrImages.data ?? []}
            isLoading={ecrImages.isLoading}
            pagination={{
              pageInfo: ecrImages.page.info,
              onChange: info =>
                dispatch(actions.updateECRImagesPagination(info)),
              totalCount: ecrImages.page.totalCount,
              cursorBased: useVulnerabilityV3,
            }}
            onRowClick={row => {
              showVulnerabilities.onOpen();
              setCurrentRecord(row);
            }}
            cursor="pointer"
            /*subComponent={({ row }) => (
                    <VulnerabilityDetails
                      vulnerabilityType={'library'}
                      total={sum(Object.keys(row?.vulnerabilities))}
                      instanceId={row?.image_digest}
                    />
                  ),
                }*/
          />
        </Box>
        {showContainers.isOpen && (
          <ImageContainers
            isOpen={showContainers.isOpen}
            row={currentRecord}
            onClose={showContainers.onClose}
          />
        )}
        {showVulnerabilities.isOpen && (
          <Drawer
            isOpen={showVulnerabilities.isOpen}
            onClose={showVulnerabilities.onClose}
            body={<ResourceVulnerabilityDetailsTable isContainer={false} />}
            header={
              <StackedHeader
                upper={
                  (currentRecord?.name ??
                    join(currentRecord?.image_tags, ', ')) +
                  ' (Repository Name: ' +
                  resourceInsight.data?.name +
                  ')'
                }
                lower={'Image Digest: ' + currentRecord?.image_digest}
                type={NativeResources.ElasticContainerRegistry}
              />
            }
            styles={{ content: { maxW: '1390px' } }}
          />
        )}
      </Stack>
    </Box>
  );
};
